import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Permission } from '@construction/shared';
import { cn } from '@/lib/cn';
import { drawingsApi, type Drawing, type DrawingPin } from '@/api/endpoints';
import {
  Alert,
  Button,
  Card,
  DetailSection,
  Drawer,
  EmptyState,
  Field,
  FilterBar,
  Input,
  ListGroup,
  ListRow,
  PageHeader,
  Spinner,
  Tag,
  Textarea,
  Timeline,
} from '@/components/ui';
import { FullscreenViewer } from '@/features/drawings/FullscreenViewer';
import { LazyMediaViewer } from '@/features/drawings/LazyViewer';
import { PinDetail, PinList } from '@/features/drawings/PinList';
import { UploadForm } from '@/features/drawings/UploadForm';
import { FilterChip } from '@/features/filters/FilterChip';
import { formatFileSize } from '@/lib/format';
import { useErrorMessage } from '@/hooks/useErrorMessage';
import { useMobileShell } from '@/hooks/useMobileShell';
import { useMobileSite } from '@/hooks/useCurrentSite';
import { DownloadButton } from '@/features/exports/DownloadButton';
import { DrawingDownload } from '@/features/drawings/DrawingDownload';
import { usePermissions } from '@/lib/permissions';

/**
 * 图纸：网页端和 App 共用这一页（CR-044，CR-082 按示例重做）。
 *
 * 客户原话：「不要资料这个合拼页面了。全部在首页 图纸，进度表，这两个就单独进对应的页面，
 * 其实图纸就是你说的管理图纸，对不」。一模块一页（同 CR-042）：
 * 上传、加删图钉按 `drawing.upload` 出现；只看的人看得到图钉内容（CR-023）；下载按 `drawing.export`。
 *
 * CR-082：分类胶囊 + 搜索；电脑上是卡片网格，手机上是列表。**点一张先在右侧（手机底部）预览** ——
 * 缩略图、图钉清单、版本历史、下载 —— 再「全屏看图」进查看器（加图钉在查看器里）。上传也在面板里。
 */
export function DrawingsPage() {
  const { t } = useTranslation();
  const toMessage = useErrorMessage();
  const { can } = usePermissions();
  const isMobileShell = useMobileShell();
  // 问权限而不是比角色名：CR-011 加 ADMIN 那次这里被漏掉了，
  // 管理者一直传不了图纸，而 === 比较不像穷举映射那样会被类型检查抓住。
  const canUpload = can(Permission.DRAWING_UPLOAD);
  const canExport = can(Permission.DRAWING_EXPORT);

  // 网页端工地由路由决定（CR-008）；App 里没有路由工地，跟着在班锁定走（约束 9）。
  // 不能用 useCurrentSite：它在 App 里只看记住的工地，工人打了上班卡照样能看别的工地的图
  const { site, siteId: effectiveSiteId } = useMobileSite();

  const [uploading, setUploading] = useState(false);
  const [previewId, setPreviewId] = useState<string | null>(null);
  const [viewing, setViewing] = useState<Drawing | null>(null);
  const [category, setCategory] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  const drawings = useQuery({
    queryKey: ['drawings', effectiveSiteId],
    queryFn: () => drawingsApi.list(effectiveSiteId!, { kind: 'DRAWING' }),
    enabled: effectiveSiteId !== null,
  });

  const all = drawings.data ?? [];
  const categories = [...new Set(all.map((d) => d.category).filter((c): c is string => Boolean(c)))];
  const query = search.trim().toLowerCase();
  const list = all.filter(
    (d) => (category === null || d.category === category) && (!query || d.title.toLowerCase().includes(query)),
  );
  const pinTotal = all.reduce((sum, d) => sum + (d._count?.pins ?? 0), 0);
  const preview = all.find((d) => d.id === previewId) ?? null;

  return (
    <>
      <PageHeader
        title={t('nav.drawings')}
        description={site?.name}
        action={
          <>
            {/* 整个工地的现行图纸打包（CR-043） */}
            {canExport && effectiveSiteId !== null && all.length > 0 && (
              <DownloadButton
                path={`/sites/${effectiveSiteId}/exports/drawings`}
                label={t('export.downloadAll')}
                action="drawing.export"
              />
            )}
            {canUpload && (
              <Button onClick={() => setUploading(true)} disabled={!effectiveSiteId}>
                {t('drawings.upload')}
              </Button>
            )}
          </>
        }
      />

      {all.length > 0 && (
        <FilterBar
          summary={
            <span className="flex gap-x-4">
              <span>{t('drawings.countSummary', { count: all.length })}</span>
              {pinTotal > 0 && <span>{t('drawings.pinCount', { count: pinTotal })}</span>}
            </span>
          }
        >
          {categories.length > 0 && (
            <FilterChip<string>
              label={t('drawings.category')}
              value={category}
              onChange={setCategory}
              options={[{ value: null, label: t('drawings.allCategories') }, ...categories.map((c) => ({ value: c, label: c }))]}
            />
          )}
          <Input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('drawings.searchPlaceholder')}
            className="w-56 max-md:w-full"
          />
        </FilterBar>
      )}

      {drawings.isLoading && (
        <div className="flex justify-center py-16 text-ink-3">
          <Spinner />
        </div>
      )}
      {drawings.isError && <Alert>{toMessage(drawings.error)}</Alert>}
      {drawings.data && drawings.data.length === 0 && (
        <EmptyState title={t('drawings.empty')} hint={canUpload ? t('drawings.emptyHint') : t('drawings.emptyHintWorker')} />
      )}

      {list.length > 0 &&
        (isMobileShell ? (
          <ListGroup>
            {list.map((drawing) => (
              <ListRow
                key={drawing.id}
                onClick={() => setPreviewId(drawing.id)}
                leading={<DrawingThumb drawing={drawing} className="size-12 rounded-lg border border-rule" />}
                title={drawing.title}
                subtitle={<DrawingMeta drawing={drawing} />}
                trailing={drawing._count?.pins ? t('drawings.pinCount', { count: drawing._count.pins }) : undefined}
              />
            ))}
          </ListGroup>
        ) : (
          <ul className="grid grid-cols-2 gap-3 lg:grid-cols-3 2xl:grid-cols-4">
            {list.map((drawing) => (
              <li key={drawing.id}>
                <button type="button" onClick={() => setPreviewId(drawing.id)} className="block w-full text-left">
                  <Card
                    className={cn(
                      'overflow-hidden transition-colors hover:border-rule-strong',
                      previewId === drawing.id && 'border-brand-500',
                    )}
                  >
                    <DrawingThumb drawing={drawing} className="aspect-[3/2] w-full border-b border-rule" />
                    <div className="px-3 py-2.5">
                      <span className="block truncate text-body font-semibold text-ink">{drawing.title}</span>
                      <span className="mt-0.5 block truncate text-caption text-ink-3">
                        <DrawingMeta drawing={drawing} />
                      </span>
                    </div>
                  </Card>
                </button>
              </li>
            ))}
          </ul>
        ))}

      <DrawingPreview
        drawing={preview}
        canExport={canExport}
        onClose={() => setPreviewId(null)}
        onOpen={(drawing) => setViewing(drawing)}
      />

      {canUpload && effectiveSiteId !== null && (
        <Drawer open={uploading} onClose={() => setUploading(false)} title={t('drawings.upload')} subtitle={site?.name} size="wide">
          <UploadForm siteId={effectiveSiteId} onDone={() => setUploading(false)} existing={all} bare />
        </Drawer>
      )}

      {viewing && <DrawingViewer drawing={viewing} onClose={() => setViewing(null)} />}
    </>
  );
}

/** 「v2 · 建筑 · 2.1 MB · 9/12」 */
function DrawingMeta({ drawing }: { drawing: Drawing }) {
  const { t, i18n } = useTranslation();
  return (
    <span className="inline-flex flex-wrap items-center gap-x-1.5">
      <Tag tone="neutral">v{drawing.version}</Tag>
      {drawing.category && <span>{drawing.category}</span>}
      <span>· {drawing.mimeType === 'application/pdf' ? 'PDF' : t('drawings.image')}</span>
      <span className="tabular-nums">· {formatFileSize(drawing.sizeBytes)}</span>
      <span className="tabular-nums">· {new Date(drawing.createdAt).toLocaleDateString(i18n.language, { month: 'numeric', day: 'numeric' })}</span>
    </span>
  );
}

/**
 * 预览图：图片有缩略图就放缩略图；PDF 没有，画一块浅色格子 + 「PDF · N 页」，不留空白。
 */
function DrawingThumb({ drawing, className }: { drawing: Drawing; className?: string }) {
  const { t } = useTranslation();
  const [failed, setFailed] = useState(false);
  const src = drawing.thumbUrl ?? (drawing.mimeType.startsWith('image/') ? drawing.url : null);
  if (src && !failed) {
    return <img src={src} alt="" loading="lazy" onError={() => setFailed(true)} className={cn('bg-surface-2 object-cover', className)} />;
  }
  return (
    <span
      className={cn(
        'flex items-center justify-center bg-surface-2 text-micro font-medium text-ink-3',
        '[background-image:linear-gradient(var(--rule)_1px,transparent_1px),linear-gradient(90deg,var(--rule)_1px,transparent_1px)] [background-size:14px_14px]',
        className,
      )}
    >
      <span className="rounded bg-surface px-1.5 py-0.5">
        {drawing.mimeType === 'application/pdf' ? `PDF${drawing.pageCount ? ` · ${drawing.pageCount}` : ''}` : t('drawings.noThumb')}
      </span>
    </span>
  );
}

/** 点一张先预览（CR-082）：缩略图、图钉、版本，底部 下载 · 全屏看图 */
function DrawingPreview({
  drawing,
  canExport,
  onClose,
  onOpen,
}: {
  drawing: Drawing | null;
  canExport: boolean;
  onClose: () => void;
  onOpen: (drawing: Drawing) => void;
}) {
  const { t, i18n } = useTranslation();
  const detail = useQuery({
    queryKey: ['drawing', drawing?.id],
    queryFn: () => drawingsApi.get(drawing!.id),
    enabled: drawing !== null,
  });
  const versions = useQuery({
    queryKey: ['drawing-versions', drawing?.id],
    queryFn: () => drawingsApi.versions(drawing!.id),
    enabled: drawing !== null && drawing.version > 1,
  });
  const pins = detail.data?.pins ?? [];

  return (
    <Drawer
      open={drawing !== null}
      onClose={onClose}
      title={drawing?.title ?? ''}
      subtitle={drawing && <DrawingMeta drawing={drawing} />}
      footer={
        drawing && (
          <>
            {canExport && <DrawingDownload drawing={drawing} />}
            <Button
              onClick={() => {
                onClose();
                onOpen(drawing);
              }}
            >
              {t('drawings.openFullscreen')}
            </Button>
          </>
        )
      }
    >
      {drawing && (
        <div className="flex flex-col gap-5">
          <button type="button" onClick={() => { onClose(); onOpen(drawing); }} className="block w-full overflow-hidden rounded-lg border border-rule">
            <DrawingThumb drawing={drawing} className="aspect-[4/3] w-full" />
          </button>

          {pins.length > 0 && (
            <DetailSection title={`${t('drawings.pinsTitle')} ${pins.length}`}>
              <ol className="flex flex-col gap-2">
                {pins.map((pin, index) => (
                  <li key={pin.id} className="flex gap-2.5 text-body">
                    <span className="flex size-5 shrink-0 items-center justify-center rounded-full bg-safety-500 text-micro font-semibold text-white">
                      {index + 1}
                    </span>
                    <span className="min-w-0">
                      <span className="block font-medium text-ink">{pin.label}</span>
                      {pin.note && <span className="block text-ink-3">{pin.note}</span>}
                    </span>
                  </li>
                ))}
              </ol>
            </DetailSection>
          )}

          <DetailSection title={t('drawings.versions')}>
            <Timeline
              items={(versions.data ?? [{ id: drawing.id, version: drawing.version, title: drawing.title, createdAt: drawing.createdAt }])
                .slice()
                .sort((a, b) => b.version - a.version)
                .map((v, index) => ({
                  key: v.id,
                  title: `v${v.version}${index === 0 ? ` · ${t('drawings.current')}` : ''}`,
                  meta: new Date(v.createdAt).toLocaleDateString(i18n.language),
                  tone: index === 0 ? 'done' : 'todo',
                }))}
            />
          </DetailSection>
        </div>
      )}
    </Drawer>
  );
}

function DrawingViewer({ drawing, onClose }: { drawing: Drawing; onClose: () => void }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const toMessage = useErrorMessage();
  const [error, setError] = useState<string | null>(null);
  const [pinMode, setPinMode] = useState(false);
  const [page, setPage] = useState(1);
  const [selectedPin, setSelectedPin] = useState<DrawingPin | null>(null);
  // 能传图纸才能加删图钉（CR-021 / CR-040）；只能看的人看得到图钉内容，没有按钮
  const canPin = usePermissions().can(Permission.DRAWING_UPLOAD);

  const detail = useQuery({
    queryKey: ['drawing', drawing.id],
    queryFn: () => drawingsApi.get(drawing.id),
  });

  const versions = useQuery({
    queryKey: ['drawing-versions', drawing.id],
    queryFn: () => drawingsApi.versions(drawing.id),
    enabled: drawing.version > 1,
  });

  // 点了图纸之后先不发请求，把落点存着，等表单填完再一起提交 ——
  // 改之前是 window.prompt 问一句标签就提交，既没法带「关联任务」，
  // 在 Capacitor 的 WebView 里样式也不可控
  const [pendingPin, setPendingPin] = useState<{ page: number; x: number; y: number } | null>(null);
  const [pinLabel, setPinLabel] = useState('');
  const [pinNote, setPinNote] = useState('');

  const addPin = useMutation({
    mutationFn: () =>
      drawingsApi.addPin(drawing.id, {
        page: pendingPin!.page,
        x: pendingPin!.x,
        y: pendingPin!.y,
        label: pinLabel.trim(),
        note: pinNote.trim() || undefined,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['drawing', drawing.id] });
      setPendingPin(null);
      setPinLabel('');
      setPinNote('');
      setPinMode(false);
    },
    onError: (err) => setError(toMessage(err)),
  });

  const removePin = useMutation({
    mutationFn: (pinId: string) => drawingsApi.removePin(pinId),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['drawing', drawing.id] }),
    onError: (err) => setError(toMessage(err)),
  });

  const pins = detail.data?.pins ?? [];
  const pagePins = pins.filter((pin: DrawingPin) => pin.page === page);

  const selectPin = (pin: DrawingPin) => {
    setPage(pin.page);
    setSelectedPin((current) => (current?.id === pin.id ? null : pin));
  };

  return (
    <FullscreenViewer
      title={drawing.title}
      meta={
        <>
          v{drawing.version}
          {drawing.category && ` · ${drawing.category}`}
          {versions.data && versions.data.length > 1 && (
            <span className="ml-2">
              {t('drawings.versionCount', { count: versions.data.length })}
            </span>
          )}
        </>
      }
      actions={
        canPin ? (
        <button
          type="button"
          onClick={() => setPinMode((v) => !v)}
          className={cn(
            'flex min-h-touch items-center rounded-md border px-3 text-body',
            pinMode ? 'border-safety-500 bg-safety-500 text-white' : 'border-white/30 text-white',
          )}
        >
          {pinMode ? t('drawings.pinModeOn') : t('drawings.addPin')}
        </button>
        ) : undefined
      }
      aside={
        error || pagePins.length > 0 ? (
          <>
            {error && <Alert>{error}</Alert>}

            <PinList
              pins={pins}
              page={page}
              selectedPinId={selectedPin?.id ?? null}
              onSelect={selectPin}
            />

            {selectedPin && (
              <PinDetail
                pin={selectedPin}
                index={
                  pagePins.findIndex((p: DrawingPin) => p.id === selectedPin.id) + 1
                }
                onRemove={
                  canPin
                    ? () => {
                        removePin.mutate(selectedPin.id);
                        setSelectedPin(null);
                      }
                    : undefined
                }
              />
            )}
          </>
        ) : undefined
      }
      // 抽屉开着时 ESC 只关抽屉：半张填好的图钉表单不该被整个查看器带走
      allowEscapeClose={pendingPin === null}
      onClose={onClose}
    >
      {detail.isLoading ? (
        <div className="flex h-full items-center justify-center text-ink-3">
          <Spinner />
        </div>
      ) : (
        <LazyMediaViewer
          url={detail.data?.url ?? drawing.url}
          mimeType={drawing.mimeType}
          pins={pins}
          page={page}
          onPageChange={setPage}
          selectedPinId={selectedPin?.id ?? null}
          onAddPin={
            pinMode
              ? (p: number, x: number, y: number) => setPendingPin({ page: p, x, y })
              : undefined
          }
          onPinClick={selectPin}
        />
      )}

      {/*
        加图钉的表单。**必须渲染在全屏元素里面** ——
        一个元素进了全屏，屏幕上就只剩它和它的后代，
        放在外面的抽屉会整个看不见。它自己是 z-drawer，
        盖在 z-sheet 的查看器上面。
      */}
      <Drawer
        open={pendingPin !== null}
        title={t('drawings.addPinTitle')}
        onClose={() => setPendingPin(null)}
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            setError(null);
            if (!pinLabel.trim()) return;
            addPin.mutate();
          }}
        >
          <Field label={t('drawings.pinLabel')} htmlFor="pin-label" required>
            <Input
              id="pin-label"
              value={pinLabel}
              onChange={(e) => setPinLabel(e.target.value)}
              maxLength={100}
              required
            />
          </Field>

          <Field label={t('drawings.pinNote')} htmlFor="pin-note" hint={t('drawings.pinNoteHint')}>
            <Textarea
              id="pin-note"
              value={pinNote}
              onChange={(e) => setPinNote(e.target.value)}
              maxLength={1000}
            />
          </Field>

          <div className="flex gap-3">
            <Button type="submit" loading={addPin.isPending}>
              {t('app.save')}
            </Button>
            <Button type="button" variant="secondary" onClick={() => setPendingPin(null)}>
              {t('app.cancel')}
            </Button>
          </div>
        </form>
      </Drawer>
    </FullscreenViewer>
  );
}
